import { z } from "zod";
import {
  InventoryId,
  IsoTimestamp,
  MatchId,
  NightId,
  PlayerId,
  RollId,
  SessionId,
  VaultItemId,
} from "./ids.js";
import {
  DisputeResolver,
  DisputeTier,
  LinkIngestMethod,
  MatchScore,
  VarVerdict,
} from "./match.js";
import { VaultItemType } from "./vault.js";
import { MagicLinkPurpose, SmsTemplateId } from "./auth.js";
import { FlagKey } from "./config.js";

// ─────────────────────────────────────────────────────────────────────────────
// THE EVENT TAXONOMY v0.2 — single source, FAIR RESULT ledger vocabulary.
// Every state transition in transitions.ts maps to one of these; the CI
// assertion (transitions.test.ts) enforces it. No event ships outside this file.
// ─────────────────────────────────────────────────────────────────────────────
export const EventType = z.enum([
  // match — card flow (FAIR RESULT §2 ledger names)
  "card_posted",
  "card_live",
  "card_claimed",
  "card_expired",
  "card_withdrawn",
  "link_opened",
  "result_window_opened",
  "result_submitted",
  "result_edited",
  "result_verified",
  "dispute_raised",
  "dispute_resolved",
  "walkover_awarded",
  "match_voided",
  // match — scheduled flow
  "match_paired",
  "ready_check_opened",
  "match_lobby_armed",
  // match — non-transition telemetry
  "match_link_ingested",
  "noshow_reported",
  "foreground_signal",
  "evidence_uploaded",
  "takeover_shown",
  "takeover_join_tapped",
  "takeover_released",
  "takeover_expired",
  // night lifecycle
  "night_opened",
  "night_closing",
  "night_closed",
  "night_cancelled",
  // vault lifecycle (THE VAULT §2)
  "vault_item_granted",
  "vault_item_ready",
  "vault_claim_started",
  "vault_fulfilled",
  "vault_fulfil_failed",
  "vault_claim_retried",
  "vault_converted",
  "vault_escalated",
  "vault_expiring",
  "vault_expired",
  // voucher chain of custody
  "voucher_reserved",
  "voucher_assigned",
  "voucher_released",
  "voucher_redeemed",
  "voucher_expired",
  "voucher_voided",
  // rewarding (MOMENTS)
  "drop_rolled",
  "drop_revealed",
  "boost_armed",
  "boost_consumed",
  "points_awarded",
  "coins_earned",
  "coins_spent",
  "confidence_changed",
  "store_order_placed",
  "founder_granted",
  // onboarding (FIRST NIGHT — Loop 2)
  "onboarding_started",
  "badge_selected",
  "avatar_purchased",
  "gamertag_set",
  "ready_earned",
  "moderation_blocked",
  // auth + messaging
  "otp_requested",
  "otp_verified",
  // THE NIGHT WATCHMAN (24 Jul): agent-assisted OTP — every reveal is on the ledger.
  "otp_support_requested",
  "otp_support_revealed",
  "magic_link_issued",
  "magic_link_redeemed",
  "session_refreshed",
  "sms_dlr_received",
  // app/session
  "session_started",
  "session_ended",
  "screen_viewed",
  "chat_message_sent",
  "quickchat_used",
  "ceremony_played",
  "perf_vitals_reported",
  // trust layer (VAR / FAIR RESULT — Loop 4)
  "evidence_analyzed",
  "agent_verdict_recorded",
  "fraud_flagged",
  // tournament engine (Loop 4B — DUGOUT S3)
  "tournament_created",
  "tournament_published",
  "tournament_started",
  "tournament_completed",
  "tournament_cancelled",
  "tournament_entered",
  "tournament_entry_refunded",
  "bracket_advanced",
  "bracket_repaired",
  "tournament_payout_queued",
  // the voice (Loop 5 — TOUCHLINE: notifications + Night Room)
  "notification_created",
  "notification_read",
  "notification_acted",
  "push_subscribed",
  "push_sent",
  "push_failed",
  "channel_decided",
  "room_message_posted",
  "room_broadcast_posted",
  "room_message_reported",
  "room_player_muted",
  "room_frozen",
  // ops
  "flag_flipped",
  "kitman_gate_blocked",
  "kitman_scope_probe",
]);
export type EventType = z.infer<typeof EventType>;

// ── Per-event payload schemas ────────────────────────────────────────────────
const matchRef = { match_id: MatchId, night_id: NightId };

export const EVENT_PAYLOADS = {
  // card flow
  card_posted: z.object({ ...matchRef, host_id: PlayerId, link_hash: z.string(), method: LinkIngestMethod }),
  card_live: z.object({ ...matchRef }),
  card_claimed: z.object({ ...matchRef, joiner_id: PlayerId }),
  card_expired: z.object({ ...matchRef }),
  card_withdrawn: z.object({ ...matchRef, by: PlayerId, count_tonight: z.number().int().min(1) }),
  link_opened: z.object({ ...matchRef, by: PlayerId }),
  result_window_opened: z.object({ ...matchRef }),
  result_submitted: z.object({ ...matchRef, by: PlayerId, score: MatchScore, screenshot: z.boolean() }),
  result_edited: z.object({ ...matchRef, by: PlayerId, from_score: MatchScore, to_score: MatchScore }),
  result_verified: z.object({ ...matchRef, by: PlayerId.nullable(), score: MatchScore, auto: z.boolean() }),
  dispute_raised: z.object({ ...matchRef, by: PlayerId }),
  dispute_resolved: z.object({
    ...matchRef,
    verdict: VarVerdict,
    tier: DisputeTier,
    resolver: DisputeResolver,
    reason_code: z.string(),
  }),
  walkover_awarded: z.object({
    ...matchRef,
    awarded_to: PlayerId,
    absent_player: PlayerId.nullable(),
    reason_code: z.string(),
  }),
  match_voided: z.object({ ...matchRef, reason_code: z.string() }),
  // scheduled flow
  match_paired: z.object({ ...matchRef, host_id: PlayerId, joiner_id: PlayerId }),
  ready_check_opened: z.object({ ...matchRef }),
  match_lobby_armed: z.object({ ...matchRef, link_hash: z.string() }),
  // match telemetry
  match_link_ingested: z.object({ ...matchRef, method: LinkIngestMethod }),
  noshow_reported: z.object({ ...matchRef, reported_by: PlayerId }),
  foreground_signal: z.object({ ...matchRef, by: PlayerId }),
  evidence_uploaded: z.object({ ...matchRef, by: PlayerId, sha256: z.string().length(64), bytes: z.number().int().min(1) }),
  takeover_shown: z.object({ ...matchRef, ms_since_join: z.number().min(0) }),
  takeover_join_tapped: z.object({ ...matchRef, ms_on_screen: z.number().min(0) }),
  takeover_released: z.object({ ...matchRef }),
  takeover_expired: z.object({ ...matchRef }),

  night_opened: z.object({ night_id: NightId }),
  night_closing: z.object({ night_id: NightId }),
  night_closed: z.object({ night_id: NightId, closed_by: z.string() }),
  night_cancelled: z.object({ night_id: NightId, reason_code: z.string() }),

  vault_item_granted: z.object({
    vault_item_id: VaultItemId,
    player_id: PlayerId,
    type: VaultItemType,
    value_minor: z.number().int().min(0),
  }),
  vault_item_ready: z.object({ vault_item_id: VaultItemId }),
  vault_claim_started: z.object({ vault_item_id: VaultItemId, player_id: PlayerId }),
  vault_fulfilled: z.object({ vault_item_id: VaultItemId, fulfilment_ref: z.string() }),
  vault_fulfil_failed: z.object({ vault_item_id: VaultItemId, reason_code: z.string(), attempt: z.number().int().min(1) }),
  vault_claim_retried: z.object({ vault_item_id: VaultItemId, attempt: z.number().int().min(2) }),
  vault_converted: z.object({ vault_item_id: VaultItemId, coins_granted: z.number().int().min(0), reason_code: z.string() }),
  vault_escalated: z.object({ vault_item_id: VaultItemId, reason_code: z.string() }),
  vault_expiring: z.object({ vault_item_id: VaultItemId, expires_at: IsoTimestamp }),
  vault_expired: z.object({ vault_item_id: VaultItemId }),

  voucher_reserved: z.object({ inventory_id: InventoryId, vault_item_id: VaultItemId }),
  voucher_assigned: z.object({ inventory_id: InventoryId, vault_item_id: VaultItemId }),
  voucher_released: z.object({ inventory_id: InventoryId, reason_code: z.string() }),
  voucher_redeemed: z.object({ inventory_id: InventoryId }),
  voucher_expired: z.object({ inventory_id: InventoryId }),
  voucher_voided: z.object({ inventory_id: InventoryId, actor: z.string(), reason_code: z.string() }),

  drop_rolled: z.object({
    roll_id: RollId,
    player_id: PlayerId,
    night_id: NightId,
    moment: z.string(),
    featured: z.boolean(),
    pool_checked: z.boolean(), // INVARIANT: pre-roll pool availability check happened
  }),
  drop_revealed: z.object({ roll_id: RollId, player_id: PlayerId }),
  boost_armed: z.object({ player_id: PlayerId, night_id: NightId, rank_at_roll: z.number().int().min(21) }),
  boost_consumed: z.object({ player_id: PlayerId, night_id: NightId, match_id: MatchId, won: z.boolean() }),
  points_awarded: z.object({
    player_id: PlayerId,
    night_id: NightId,
    delta: z.number().int(),
    reason_code: z.string(),
    match_id: MatchId.nullable(),
  }),
  coins_earned: z.object({ player_id: PlayerId, delta: z.number().int().positive(), reason_code: z.string(), source_ref: z.string() }),
  coins_spent: z.object({ player_id: PlayerId, delta: z.number().int().negative(), reason_code: z.string(), source_ref: z.string() }),
  confidence_changed: z.object({ player_id: PlayerId, delta: z.number().int(), reason_code: z.string(), match_id: MatchId.nullable() }),
  store_order_placed: z.object({ player_id: PlayerId, item_id: z.string().uuid(), coins_paid: z.number().int(), vault_item_id: VaultItemId.nullable() }),
  founder_granted: z.object({ player_id: PlayerId, retro: z.boolean() }),

  onboarding_started: z.object({ player_id: PlayerId }),
  badge_selected: z.object({ player_id: PlayerId, badge_id: z.string(), lucky: z.boolean() }),
  avatar_purchased: z.object({ player_id: PlayerId, avatar_id: z.string(), coins_paid: z.number().int().min(0) }),
  gamertag_set: z.object({ player_id: PlayerId, gamertag: z.string() }),
  ready_earned: z.object({ player_id: PlayerId }),
  moderation_blocked: z.object({ player_id: PlayerId.nullable(), surface: z.enum(["gamertag", "chat"]), reason: z.string() }),

  otp_requested: z.object({ msisdn_hash: z.string(), template_id: SmsTemplateId }),
  otp_verified: z.object({ msisdn_hash: z.string(), player_id: PlayerId }),
  otp_support_requested: z.object({ msisdn_hash: z.string() }),
  otp_support_revealed: z.object({ msisdn_hash: z.string(), operator: z.string() }),
  magic_link_issued: z.object({ purpose: MagicLinkPurpose, match_id: MatchId.nullable() }),
  magic_link_redeemed: z.object({ purpose: MagicLinkPurpose, match_id: MatchId.nullable(), player_id: PlayerId }),
  session_refreshed: z.object({ player_id: PlayerId, session_id: SessionId }),
  sms_dlr_received: z.object({ provider_message_id: z.string(), status: z.string() }),

  session_started: z.object({ player_id: PlayerId.nullable(), session_id: SessionId, source: z.string() }),
  session_ended: z.object({ session_id: SessionId, screens_viewed: z.number().int().min(0) }),
  screen_viewed: z.object({ session_id: SessionId, route: z.string() }),
  chat_message_sent: z.object({ match_id: MatchId.nullable(), by: PlayerId, room: z.enum(["match", "night"]), filtered: z.boolean() }),
  quickchat_used: z.object({ match_id: MatchId, by: PlayerId, preset_id: z.string() }),
  ceremony_played: z.object({
    name: z.string(),
    tier: z.enum(["FULL", "TRIM", "LITE"]),
    skipped: z.boolean(),
    ms: z.number().min(0),
  }),
  perf_vitals_reported: z.object({
    session_id: SessionId,
    route: z.string(),
    lcp: z.number().nullable(),
    tti: z.number().nullable(),
    cls: z.number().nullable(),
    device_tier: z.enum(["FULL", "TRIM", "LITE"]),
  }),

  evidence_analyzed: z.object({ match_id: MatchId, sha256: z.string().length(64), phash_collision: z.boolean() }),
  agent_verdict_recorded: z.object({ match_id: MatchId, verdict: z.string(), confidence: z.number(), mode: z.enum(["shadow", "assist", "auto"]) }),
  fraud_flagged: z.object({ signal: z.string(), subjects: z.array(PlayerId), reason_code: z.string() }),

  // tournament engine (Loop 4B)
  tournament_created: z.object({ tournament_id: z.string().uuid(), night_id: NightId, name: z.string(), size: z.number().int() }),
  tournament_published: z.object({ tournament_id: z.string().uuid(), night_id: NightId }),
  tournament_started: z.object({ tournament_id: z.string().uuid(), night_id: NightId, entrants: z.number().int() }),
  tournament_completed: z.object({ tournament_id: z.string().uuid(), night_id: NightId, champion_id: PlayerId.nullable() }),
  tournament_cancelled: z.object({ tournament_id: z.string().uuid(), night_id: NightId, reason_code: z.string() }),
  tournament_entered: z.object({ tournament_id: z.string().uuid(), player_id: PlayerId, standby: z.boolean(), entry_type: z.string() }),
  tournament_entry_refunded: z.object({ tournament_id: z.string().uuid(), player_id: PlayerId, coins: z.number().int() }),
  bracket_advanced: z.object({ tournament_id: z.string().uuid(), round: z.number().int(), slot: z.number().int(), winner_id: PlayerId, match_id: MatchId.nullable() }),
  bracket_repaired: z.object({ tournament_id: z.string().uuid(), round: z.number().int(), slot: z.number().int(), action: z.string(), reason_code: z.string() }),
  tournament_payout_queued: z.object({ tournament_id: z.string().uuid(), player_id: PlayerId, final_rank: z.number().int(), amount_minor: z.number().int(), vault_item_id: z.string().uuid() }),

  // the voice (Loop 5)
  notification_created: z.object({ notification_id: z.string().uuid(), player_id: PlayerId, key: z.string(), category: z.string() }),
  notification_read: z.object({ notification_id: z.string().uuid(), player_id: PlayerId }),
  notification_acted: z.object({ notification_id: z.string().uuid(), player_id: PlayerId, key: z.string() }),
  push_subscribed: z.object({ player_id: PlayerId }),
  push_sent: z.object({ notification_id: z.string().uuid(), player_id: PlayerId, key: z.string() }),
  push_failed: z.object({ notification_id: z.string().uuid(), player_id: PlayerId, reason: z.string() }),
  // The SMS→push migration instrumentation: one row per (notification, channel).
  channel_decided: z.object({ notification_id: z.string().uuid(), player_id: PlayerId, key: z.string(), channel: z.enum(["push", "sms"]), decision: z.string() }),
  room_message_posted: z.object({ night_id: NightId, player_id: PlayerId, filtered: z.boolean(), length: z.number().int() }),
  room_broadcast_posted: z.object({ night_id: NightId, operator: z.string(), notified: z.boolean() }),
  room_message_reported: z.object({ night_id: NightId, message_id: z.string().uuid(), reported_by: PlayerId, reason_code: z.string() }),
  room_player_muted: z.object({ night_id: NightId, player_id: PlayerId, reason_code: z.string() }),
  room_frozen: z.object({ night_id: NightId, message_count: z.number().int() }),

  flag_flipped: z.object({ key: FlagKey, enabled: z.boolean(), actor: z.string() }),
  kitman_gate_blocked: z.object({ player_id: PlayerId.nullable(), category: z.string() }),
  kitman_scope_probe: z.object({ player_id: PlayerId }),
} satisfies Record<EventType, z.ZodTypeAny>;

export type EventPayload<T extends EventType> = z.infer<(typeof EVENT_PAYLOADS)[T]>;

// The envelope the client SDK sends and the ingest service validates.
export const TrackedEvent = z.object({
  event_type: EventType,
  event_time: IsoTimestamp,
  session_id: SessionId.nullable(),
  device_hash: z.string().nullable(),
  payload: z.record(z.unknown()),
});
export type TrackedEvent = z.infer<typeof TrackedEvent>;

// ── ClickHouse row shapes (KICKOFF §4.1) ─────────────────────────────────────
export const MatchEventRow = z.object({
  event_time: IsoTimestamp,
  night_id: NightId.nullable(),
  match_id: MatchId.nullable(),
  player_id: PlayerId.nullable(),
  event_type: EventType,
  payload: z.string(), // JSON-serialised
  session_id: SessionId.nullable(),
  device_hash: z.string().nullable(),
});
export type MatchEventRow = z.infer<typeof MatchEventRow>;

export const PointsLedgerRow = z.object({
  player_id: PlayerId,
  ts: IsoTimestamp,
  night_id: NightId,
  delta: z.number().int(),
  reason_code: z.string(),
  match_id: MatchId.nullable(),
  balance_after: z.number().int(),
});
export type PointsLedgerRow = z.infer<typeof PointsLedgerRow>;

export const CoinLedgerRow = z.object({
  player_id: PlayerId,
  ts: IsoTimestamp,
  delta: z.number().int(),
  reason_code: z.string(),
  source_ref: z.string(),
  balance_after: z.number().int(),
});
export type CoinLedgerRow = z.infer<typeof CoinLedgerRow>;

export const ConfidenceLedgerRow = z.object({
  player_id: PlayerId,
  ts: IsoTimestamp,
  delta: z.number().int(),
  reason_code: z.string(),
  match_id: MatchId.nullable(),
  balance_after: z.number().int(),
});
export type ConfidenceLedgerRow = z.infer<typeof ConfidenceLedgerRow>;

export const DropEventRow = z.object({
  player_id: PlayerId,
  ts: IsoTimestamp,
  night_id: NightId,
  moment: z.string(),
  vehicle: z.string(),
  payout_type: z.string(),
  payout_value: z.number().int(),
  featured: z.boolean(),
  roll_id: RollId,
  revealed_at: IsoTimestamp.nullable(),
});
export type DropEventRow = z.infer<typeof DropEventRow>;

export const SessionRow = z.object({
  player_id: PlayerId.nullable(),
  session_id: SessionId,
  started: IsoTimestamp,
  ended: IsoTimestamp.nullable(),
  screens_viewed: z.number().int().min(0),
  source: z.string(),
});
export type SessionRow = z.infer<typeof SessionRow>;

export const PerfVitalsRow = z.object({
  ts: IsoTimestamp,
  session_id: SessionId,
  route: z.string(),
  lcp: z.number().nullable(),
  tti: z.number().nullable(),
  cls: z.number().nullable(),
  device_tier: z.enum(["FULL", "TRIM", "LITE"]),
});
export type PerfVitalsRow = z.infer<typeof PerfVitalsRow>;

export const VoucherLedgerRow = z.object({
  inventory_id: InventoryId,
  ts: IsoTimestamp,
  from_status: z.string(),
  to_status: z.string(),
  actor: z.string(),
  reason: z.string(),
});
export type VoucherLedgerRow = z.infer<typeof VoucherLedgerRow>;
